import map from 'lodash/map'
import React, { createRef, useState, VFC } from 'react'
import styled from 'styled-components'

import { Button } from '@Components/Button'
import { BuyButton } from '@Components/BuyButton'
import { TextField } from '@Components/FormElements/UI/TextField'
import { Modal } from '@Components/Modal'
import {
  PersonalizationButtonLayout,
} from '@Components/Personalization/Layout/PersonalizationButtonLayout'
import PersonalizationSelectItemLayout
  from '@Components/Personalization/Layout/PersonalizationSelectItemLayout'
import {
  PersonalisationModal,
  PersonalisationModalTypes,
} from '@Components/Personalization/Modals/Shared'
import {
  PersonalizationCaption,
  PersonalizationInfoList,
  PersonalizationItemList,
  PersonalizationSubTitle,
  PersonalizationTitle,
} from '@Components/Personalization/Styles/Personalization.styled'
import { stripEmoji, stripWhitespaces } from '@Components/Personalization/Util/helpers'
import {
  getPersonalizationsByType,
  getPersonalizationsFromProduct,
} from '@Globals/CMS/ProductHelpers'
import { useTranslation } from '@Globals/Translations'

interface Props extends PersonalisationModal {
  info: {
    list: string[]
    title: string
  }
  selectedVariantID?: string
  type: PersonalisationModalTypes
  updateProduct?: boolean
}

const StyledBuyButton = styled(props => <BuyButton {...props} />)`
  margin-bottom: 12px;
`

const StyledTextField = styled(TextField)`
  margin-bottom: 7.5px;
  font-size: 1rem;

  label {
    margin-top: 12px;
    font-size: 0.9rem;
  }
`

const StyledSubTitle = styled(PersonalizationSubTitle)`
  margin-bottom: 13px;
`

export const PersonalisationModalLayout: VFC<Props> = ({
  additionalIDs,
  info,
  isChildOfModal,
  isOpen,
  onHide,
  product,
  selectedVariantID = '',
  type,
  updateProduct = false,
}) => {
  const { t } = useTranslation()
  const [selectedPlacement, setSelectedPlacement] = useState<number>(0)
  const [validText, setValidText] = useState<boolean>(true)
  const textRef = createRef<HTMLInputElement>()
  const productPersonalizations = getPersonalizationsFromProduct(product)
  const personalizations = getPersonalizationsByType(productPersonalizations, type)
  const [disabled, setDisabled] = useState(true)
  if (!personalizations) {
    return null
  }
  const getMaxNumberOfCharacters = (placement: number) => Number(personalizations[placement].maxCharacters)

  const ModalContent = (
    <>
      <PersonalizationTitle>{t(info.title)}</PersonalizationTitle>
      <PersonalizationCaption>{t('products:MAKE_IT_UNIQUE')}</PersonalizationCaption>
      <StyledSubTitle left>
        {t('products:PERSONALISATION_PLACEMENT')}
      </StyledSubTitle>

      <PersonalizationItemList>
        {map(personalizations, ({
          image, placement,
          price,
        }, index) => (
          <PersonalizationSelectItemLayout
            key={`${placement}-${index}`}
            buttonContent={(
              <PersonalizationButtonLayout
                placement={placement}
                price={price}
              />
            )}
            image={image}
            withSelect={{
              onSelect: () => {
                setSelectedPlacement(index)
                if (textRef?.current?.value) {
                  textRef.current.value = textRef.current.value.slice(0, getMaxNumberOfCharacters(index))
                }
              },
              selected: selectedPlacement === index,
            }}
          />
        ))}
      </PersonalizationItemList>

      <PersonalizationSubTitle left>{t('products:ADD_YOUR_TEXT_HERE')}</PersonalizationSubTitle>

      <StyledTextField
        error={!validText ? t('common:THIS_FIELD_IS_REQUIRED') : ''}
        id="text"
        label={t('TEXT_MAX_X_CHARACTERS').replace('<span>X</span>', `${personalizations[selectedPlacement].maxCharacters}`)}
        maxLength={getMaxNumberOfCharacters(selectedPlacement)}
        onChange={(event) => {
          if (textRef.current) {
            event.preventDefault()
            textRef!.current!.value = stripEmoji(event.target.value)
            const isAllWhitespace = stripWhitespaces(textRef!.current!.value).length === 0
            setValidText(!isAllWhitespace)
            const disabledValue = event.target.value.length === 0 || isAllWhitespace
            setDisabled(disabledValue)
          }
        }}
        ref={textRef}
      />
      <StyledBuyButton
        additionalIDs={additionalIDs}
        beforeAddToCart={onHide}
        buttonText={t('cms:ADD_TO_CART')}
        disabled={disabled}
        getPersonalizationData={() => ({
          id: personalizations[selectedPlacement].id,
          text: textRef.current?.value,
        })}
        isSaleable
        product={product}
        showPrice={false}
        unwrapped
        updateProduct={updateProduct}
        variantID={selectedVariantID}
      />
      <Button color="dark" onClick={onHide} variant="outlined">{t('header:CANCEL')}</Button>
      <PersonalizationInfoList>
        {map(info.list, wordbook => <li key={wordbook}>{t(wordbook)}</li>)}
      </PersonalizationInfoList>
    </>
  )

  if (isChildOfModal) {
    return ModalContent
  }

  return (
    <Modal isOpen={isOpen} maxWidth="440px" onHide={onHide} variant="default">
      {ModalContent}
    </Modal>
  )
}
